import React, { ReactElement } from "react";
import { Button } from "@mui/material";
import { useAppSelector } from "../../state/hooks";
import { gameOverview } from "../../state/game/game-api-slice";

/**
 * Banner shown to a visitor watching a game without playing a country.
 *
 * The API only sets the `user` object when the caller actually plays a
 * country in this game — GetGameMembers::getData() does
 *     if (isset($game->Members->ByUserID[$userID])) { $memberData['user'] = ... }
 * and $userID is null for a visitor. So `!overview.user` is precisely the
 * "spectating" condition; no extra flag is needed, and the rest of the UI
 * (orders, messages, phase controls) is already keyed off the same value.
 *
 * 观战有两种人：
 *  1) 游客（isLoggedIn = false）：需要告诉他可以注册/登录去真正下场，所以给
 *     「注册加入 / 登录」两个按钮；
 *  2) 已登录但本局没席位（isLoggedIn = true）：他已经能登录了，再给注册登录
 *     按钮纯属噪音，只留回大厅的出口。
 *
 * Links are plain <a> rather than react-router: the board app has no router
 * installed, and register.php / logon.php live outside the React bundle.
 */
const WDSpectatorBanner = function (): ReactElement | null {
  const { user, name, isLoggedIn } = useAppSelector(gameOverview);

  // 已登录且是本局玩家，不是观战者，不需要提示条。
  // 返回类型因此是 ReactElement | null —— 组件要么渲染横幅、要么什么都不渲染。
  if (user) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 40,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        // 右侧留出右上角 logo（也是回主界面的入口）的位置，别把它盖住。
        padding: "8px 76px 8px 12px",
        background: "#16130e",
        color: "#f2ecdf",
        fontFamily: '"Noto Serif SC", Georgia, serif',
        fontSize: "14px",
        lineHeight: 1.4,
      }}
    >
      <span>
        你正在观战
        {name ? (
          <>
            {" · "}
            <strong>{name}</strong>
          </>
        ) : null}
      </span>
      <span style={{ opacity: 0.7, fontSize: "13px" }}>
        {isLoggedIn ? "本局你没有席位，只能看不能下令" : "观战不需要注册"}
      </span>
      {isLoggedIn ? (
        <Button
          href="/gamelistings.php?gamelistType=My%20games"
          size="small"
          variant="outlined"
          disableElevation
          sx={{
            borderColor: "rgba(242,236,223,.42)",
            color: "#f2ecdf",
            borderRadius: "3px",
            minHeight: "34px",
            textTransform: "none",
            "&:hover": {
              borderColor: "#f2ecdf",
              background: "rgba(242,236,223,.08)",
            },
          }}
        >
          我的对局
        </Button>
      ) : (
        <>
          <Button
            href="/register.php"
            size="small"
            variant="contained"
            disableElevation
            sx={{
              background: "#9e2b25",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "3px",
              minHeight: "34px",
              textTransform: "none",
              "&:hover": { background: "#b8352c" },
            }}
          >
            注册加入
          </Button>
          <Button
            href="/logon.php"
            size="small"
            variant="outlined"
            disableElevation
            sx={{
              borderColor: "rgba(242,236,223,.42)",
              color: "#f2ecdf",
              borderRadius: "3px",
              minHeight: "34px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#f2ecdf",
                background: "rgba(242,236,223,.08)",
              },
            }}
          >
            登录
          </Button>
        </>
      )}
      <Button
        href="/gamelistings.php"
        size="small"
        variant="contained"
        disableElevation
        sx={{
          background: isLoggedIn ? "#9e2b25" : "#f2ecdf",
          color: isLoggedIn ? "#fff" : "#16130e",
          fontWeight: 700,
          borderRadius: "3px",
          minHeight: "34px",
          textTransform: "none",
          "&:hover": {
            background: isLoggedIn ? "#b8352c" : "#fff",
          },
        }}
      >
        返回大厅
      </Button>
    </div>
  );
};

export default WDSpectatorBanner;
